import {defaults, get, isEmpty} from 'lodash';
import moment from 'moment';

import internalOrgIds from 'resources/InternalOrgIds';
import IpimsLicenseType from 'resources/IpimsLicenseType';
import ProductEdition from 'resources/ProductEdition';
import Role from 'resources/Role';
import {Feature} from 'types/feature';
import {ProductOptions} from 'types/product';
import {AppData, CloudAppUser, User} from 'types/store';
import buildUserPrivileges from 'utils/buildUserPrivileges';
import getConsoleUrlFromReferrer from 'utils/getConsoleUrlFromReferrer';
import {trimTrailingSlashes} from 'utils/trimTrailingSlashes';

const unknownUser = 'unknownuser';
const getDateDiff = (ms: number) =>
  Math.floor(moment.utc(ms).diff(moment().utc(), 'days', true));

const getIsConsoleOwned = ({
  pairingKey,
  isAgentOnly,
  isProjectOnly,
}: {
  pairingKey: string;
  isAgentOnly: boolean;
  isProjectOnly: boolean;
}) => !pairingKey && !isAgentOnly && !isProjectOnly;

const buildUser = function ({
  consoleUrl,
  consoleVersion,
  isAgentOnly = false,
  isDev = false,
  isInternalOrg,
  isFromPlatform,
  orgConsoleUrl,
  productOptions: {
    consoleLicenseExpirationTime,
    has_data: agentHasData,
    ipims_session_id: ipimsSessionId,
    is_aws_customer: isAwsMember,
    pairing_key: pairingKey = '',
  },
  user,
}: {
  consoleUrl?: string;
  consoleVersion?: string;
  isAgentOnly?: boolean;
  isDev?: boolean;
  isInternalOrg?: boolean;
  isFromPlatform?: boolean;
  orgConsoleUrl?: string;
  productOptions: ProductOptions;
  user: User;
}): CloudAppUser {
  const productEdition = user['product-edition'] || ProductEdition.INSIGHT_VM;
  const daysLeft = consoleLicenseExpirationTime
    ? getDateDiff(consoleLicenseExpirationTime)
    : 0;
  const hasPlatformAccount = isFromPlatform ? true : !!user.ipimsUserId;
  const isAdmin = Role.ADMIN_ROLES.indexOf(user.role) >= 0;
  const isDisabled = user.isDisabled || false;
  const isProjectOnly =
    (isEmpty(user) &&
      window.location.href.indexOf('/remediation/projects/') !== -1) ||
    user.role === Role.PROJECT_ONLY;
  const platformEmail = user.ipimsEmail;

  delete user.ipimsEmail;

  const formattedUser = {
    ...defaults(user, {
      previewFeatures: [],
      userKey: '',
    }),
    //the session-index is the console or EA session depending on where a user authenticated from

    consoleSessionId: consoleUrl
      ? get(user, 'session-index', undefined)
      : undefined,
    disabledFeatures: user.disabledFeatures as Feature[],
    email: user.email === unknownUser ? undefined : user.email,
    hasPlatformAccount,
    ipimsSessionId,
    isAdmin,
    isDisabled,
    isPlatformAccountActive: isFromPlatform || user.isAccountActive,
    isProjectOnly,
    membership: {
      daysLeft,
      isActive: daysLeft >= 0 || daysLeft === null,
    },
    platformEmail,
    productEdition,
  };

  const isConsoleOwned = getIsConsoleOwned({
    isAgentOnly,
    isProjectOnly,
    pairingKey,
  });

  return {
    ...formattedUser,
    ...buildUserPrivileges({
      agentHasData,
      consoleUrl,
      consoleVersion,
      isAgentOnly,
      isAwsMember,
      isConsoleOwned,
      isDev,
      isInternalOrg,
      orgConsoleUrl,
      pairingKey,
      user: formattedUser,
    }),
  };
};

const isIPIMSLicenseType = function (
  {license_type: ipimsLicenseType}: {license_type: string},
  providedLicenseType: string
) {
  return !!ipimsLicenseType && ipimsLicenseType === providedLicenseType;
};

const getConsoleUrl = ({
  user,
  csrfToken,
  isFromPlatform,
  productOptions,
}: {
  user: User;
  csrfToken: string;
  isFromPlatform: boolean;
  productOptions: ProductOptions;
}) => {
  if (user.isSSOEnabled && isFromPlatform) {
    return trimTrailingSlashes(productOptions.consoleUrl ?? '');
  }

  if (isFromPlatform) {
    return '';
  }

  return trimTrailingSlashes(
    getConsoleUrlFromReferrer(user.referer, csrfToken)
  );
};

export default function build({
  user,
  licenseOptions,
  csrfToken,
  productOptions,
  isDev,
}: {
  user: User;
  licenseOptions: any;
  csrfToken: string;
  productOptions: any;
  isDev: boolean;
}): AppData {
  const isFromPlatform = !!productOptions.ipims_session_id || false;
  const pairingKey = productOptions.pairing_key || null;
  const isAgentOnly = isIPIMSLicenseType(
    productOptions,
    IpimsLicenseType.INSIGHT_VM_AGENT
  );
  const isInternalOrg = Object.values(internalOrgIds).includes(user.orgId);
  const consoleUrl = getConsoleUrl({
    csrfToken,
    isFromPlatform,
    productOptions,
    user,
  });
  const orgConsoleUrl = productOptions.consoleUrl || '';
  const consoleVersion = productOptions.consoleProductVersion || '';
  const formattedUser = buildUser({
    consoleUrl,
    consoleVersion,
    isAgentOnly,
    isDev,
    isFromPlatform,
    isInternalOrg,
    orgConsoleUrl,
    productOptions,
    user,
  });
  const isConsoleOwned = getIsConsoleOwned({
    isAgentOnly,
    isProjectOnly: formattedUser.isProjectOnly,
    pairingKey,
  });
  const agentHasData = productOptions.has_data || null;
  const isFromConsole = !!formattedUser.consoleSessionId && !isFromPlatform;

  return {
    agentHasData,
    consoleUrl,
    consoleVersion,
    csrfToken,
    ipims: {
      csrfToken: productOptions.ipims_csrf_token || null,
      licenseType: get(
        productOptions,
        'license_type',
        IpimsLicenseType.EXPOSURE_ANALYTICS
      ),
      productToken: productOptions.product_token || null,
      sessionId: productOptions.ipims_session_id || null,
    },
    isAgentOnly,
    isCollectorHealthy: user.email !== unknownUser,
    isConsoleOwned,
    isDev,
    isFromConsole,
    isFromPlatform,
    isFromR7Okta: !isFromConsole && !isFromPlatform, // a dev will be isFromR7Okta as they use Okta to login
    isInternalOrg,
    licenseOptions,
    //the console url defined for the org. This can be different than the consoleUrl above (ie after a migration). The consoleUrl should be the trusted url across the app
    orgConsoleUrl,
    pairingData: pairingKey
      ? {
          duration: parseInt(productOptions.pairing_key_duration) || null,
          key: pairingKey,
        }
      : null,
    theme: 'dark',
    user: formattedUser,
  };
}
